/**
 * SurfaceTopBar — `header.hd`, the first section of the spine.
 *
 * Built to `publisher-drop-hub.html` lines 944-963. Every class name here is
 * the anchor's, so `HubSpine.scss` styles it without a single new rule.
 *
 * ## This is the surface's OWN bar, not the platform's
 *
 * A hub-style surface leads with the thing it is about: the publisher's
 * lockup, the window it is in, and the counters that say how the community is
 * doing against it. The DevourPlay topbar answers a different question — where
 * am I in the platform — and stacking the two puts two identity bars above
 * every hub. Chrome is the CHANNEL's decision and `DropPageRouter` makes it;
 * this is content, and it belongs to the surface.
 *
 * ## Nothing here is invented
 *
 * Every slot is optional and absent renders nothing. The anchor shows a full
 * bar because MW4 has a lockup, a launch window, a signed-in member and two
 * live counters; a surface with none of that gets a bar with a name in it, not
 * a bar with placeholders. Inventing "0 contributing" to fill the shape would
 * put a number on the page that means nothing — and the whole point of these
 * counters is that they mean something.
 *
 * ## The lockup IS the title
 *
 * `.hd__logo` takes an image, and type is the FALLBACK. A publisher ships a
 * lockup no font reproduces, so setting the name in Barlow beside their
 * artwork reads as a placeholder for the logo that should be there. The
 * anchor's hero makes the same call in its own comment.
 */

import React from "react";
import {SurfaceAccountMenu, type SurfaceAccountMenuItem} from "./SurfaceAccountMenu";

/** One of the counter pairs on the right — a figure and what it counts. */
export interface SurfaceTopBarStat {
    id: string;
    /** Already formatted. This component does not decide a number's locale. */
    value: string;
    label: string;
    icon?: React.ReactNode;
}

/** The signed-in member's standing ON THIS SURFACE, not their platform account. */
export interface SurfaceTopBarMember {
    /**
     * The member's own picture. This is the avatar; the monogram below is what
     * we draw when there is no picture, not the default. The anchor shows a
     * monogram because a mock has no account behind it.
     */
    imageUrl?: string;
    /**
     * Fallback when there is no picture. Two or three characters.
     *
     * Optional: a member who has set no name has no monogram to draw, and
     * that must not disqualify them from being a member — the account menu
     * is where they go to set one.
     */
    initials?: string;
    /** May be blank. A member without a name is still a member. */
    name: string;
    /** e.g. "Tier 2". Absent renders nothing rather than "Tier —". */
    standing?: string;
    /**
     * How many badges this member has earned, shown as a count beside their
     * name. Zero and undefined both render nothing — a "0" beside a name reads
     * as a score they are losing, not as a collection they have not started.
     */
    badgeCount?: number;
    /** e.g. "340 / 1,000 Readiness". Pre-formatted, same reason as the stats. */
    progressLabel?: string;
    /**
     * 0-1. Drives the meter's width. Undefined hides the meter entirely — a
     * bar at 0% and a bar that does not apply look identical and are not.
     */
    progress?: number;
}

export interface SurfaceTopBarProps {
    /** The surface's lockup. Falls back to `name` as type when absent. */
    logoUrl?: string;
    name: string;
    /**
     * Display-type wordmark set BESIDE the lockup (`.hd__wm`) — the brand
     * mark and the campaign's name are two different identities and a bar
     * carrying a campaign shows both. Absent renders nothing; the lockup
     * alone is a complete bar.
     */
    wordmark?: string;
    /** The window the surface is in — "Launch Window" / "Reveal → Oct 23". */
    phaseLabel?: string;
    phaseDetail?: string;
    member?: SurfaceTopBarMember;
    /**
     * The account menu's contents. The avatar IS the menu — see
     * SurfaceAccountMenu — so a member with no items still gets an avatar, it
     * simply does not open.
     */
    accountItems?: readonly SurfaceAccountMenuItem[];
    /** The menu's first line — whose menu this is. */
    memberName?: string;
    /**
     * Shown inside the menu, behind a rule, under the platform mark. On a phone
     * this is where the legal group lives, because the sticky bar belongs to
     * the hub and has no room for anything that is not.
     */
    legal?: readonly SurfaceAccountMenuItem[];
    platformLogoUrl?: string;
    stats?: readonly SurfaceTopBarStat[];
    /** Trailing circular controls. Caller supplies them; this picks no icons. */
    actions?: React.ReactNode;
    /** Signed-out affordance — absent renders nothing (an embed may not want it). */
    onSignIn?: () => void;
}

export const SurfaceTopBar: React.FC<SurfaceTopBarProps> = ({
    logoUrl, name, wordmark, phaseLabel, phaseDetail, member, stats = [], actions,
    accountItems = [], legal = [], platformLogoUrl, memberName, onSignIn,
}) =>
    <header className="sh-hd">
        {logoUrl
            ? <img className="sh-hd__logo" src={logoUrl} alt={name} />
            /*
             * Type only when there is no lockup. `.hd__logo` is sized in the
             * spine as an image (height 30px, auto width); a text node in the
             * same slot needs the display treatment instead, which is what
             * `.hd__ss b` already is.
             */
            : <span className="sh-hd__ss" style={{borderLeft: "none", paddingLeft: 0}}><b>{name}</b></span>}

        {wordmark && <span className="sh-hd__wm">{wordmark}</span>}

        {phaseLabel &&
            <span className="sh-hd__ss">
                <b>{phaseLabel}</b>
                {phaseDetail && <span>{phaseDetail}</span>}
            </span>}

        <span className="sh-hd__me">
            {/*
              * ORDER: counters, then notifications, then identity, with the
              * avatar on the OUTSIDE edge — the arrangement used everywhere
              * else, so the account menu is in the corner a viewer already
              * reaches for. The bell sits INBOARD of the name because it is a
              * count about that identity, not something past it.
              *
              * (Product ruling reaffirmed 2026-08-29: the DESIGN ANCHOR had
              * this backwards — avatar inboard — and was corrected to match
              * this component, not the other way around.)
              */}
            {stats.map(stat =>
                <span className="sh-hs" key={stat.id}>
                    {stat.icon}
                    <span>
                        <b>{stat.value}</b>
                        <span>{stat.label}</span>
                    </span>
                </span>)}

            {actions}

            {/* Signed out: the bar must still offer the door. */}
            {!member && onSignIn &&
                <button type="button" className="sh-hd__signin" onClick={onSignIn}>
                    Sign In
                </button>}

            {member &&
                <>
                    <span className="sh-who">
                        <b>
                            {member.name}
                            {/*
                              * The badge count, ON the name rather than under
                              * it: it is a property of who they are, the way a
                              * rank is not. Bragging rights need to travel with
                              * the name wherever it is shown.
                              */}
                            {Boolean(member.badgeCount) &&
                                <i className="sh-who__bdg" title={`${member.badgeCount} badges earned`}>
                                    {member.badgeCount}
                                </i>}
                        </b>
                        {member.standing && <span>{member.standing}</span>}
                    </span>
                    <SurfaceAccountMenu
                        memberName={memberName}
                        imageUrl={member.imageUrl}
                        initials={member.initials}
                        progress={member.progress}
                        items={accountItems}
                        legal={legal}
                        platformLogoUrl={platformLogoUrl}
                    />
                </>}
        </span>
    </header>;
export default SurfaceTopBar;
